/**
 * GutscheinZentrale.jsx – Globale Promo-Code Verwaltung (Superadmin)
 * Erstellen, Listen, Löschen von Stripe Promotion Codes
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash2, RefreshCw, Copy, Check, Ticket, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GutscheinZentrale() {
  const [codes, setCodes] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState({
    code: '', coupon_id: '', max_redemptions: '', expires_at: ''
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [codesRes, couponsRes] = await Promise.all([
        axios.get(`${API}/api/superadmin/promo-codes`, { headers }),
        axios.get(`${API}/api/superadmin/stripe-coupons`, { headers }),
      ]);
      setCodes(codesRes.data || []);
      setCoupons(couponsRes.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.code.trim() || !form.coupon_id) {
      toast.error('Code und Rabatt-Typ sind Pflichtfelder');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/superadmin/promo-codes`, {
        code: form.code.trim().toUpperCase(),
        coupon_id: form.coupon_id,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        expires_at: form.expires_at || null,
      }, { headers });
      toast.success(`Code "${form.code.toUpperCase()}" wurde in Stripe erstellt ✅`);
      setForm({ code: '', coupon_id: '', max_redemptions: '', expires_at: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setCreating(false); }
  };

  const handleDeactivate = async (pc) => {
    if (!window.confirm(`Code "${pc.code}" deaktivieren?`)) return;
    try {
      await axios.delete(`${API}/api/superadmin/promo-codes/${pc.id}`, { headers });
      toast.success(`Code "${pc.code}" deaktiviert`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const activeCodes   = codes.filter(c => c.active);
  const inactiveCodes = codes.filter(c => !c.active);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Ticket className="h-6 w-6 text-violet-600" />
              Gutschein-Zentrale
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Erstelle und verwalte Stripe Promotion Codes für deine Kunden
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        {/* Neuen Code erstellen */}
        <Card className="border-violet-200 dark:border-violet-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-violet-700 dark:text-violet-400">
              <Plus className="h-4 w-4" /> Neuen Promo-Code erstellen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Code-Name <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="z.B. JET20, SOMMER25"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                  className="font-mono uppercase"
                  style={{ fontSize: '16px' }}
                  data-testid="promo-code-input"
                />
                <p className="text-xs text-muted-foreground">Nur Buchstaben & Zahlen, kein Leerzeichen</p>
              </div>
              <div className="space-y-1.5">
                <Label>Rabatt-Typ <span className="text-destructive">*</span></Label>
                <select
                  value={form.coupon_id}
                  onChange={e => setForm(f => ({ ...f, coupon_id: e.target.value }))}
                  className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm"
                  style={{ fontSize: '16px' }}
                  data-testid="coupon-select"
                >
                  <option value="">Coupon auswählen...</option>
                  {coupons.filter(c => c.valid !== false).map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Max. Nutzungen (optional)</Label>
                <Input
                  type="number" min="1"
                  placeholder="unbegrenzt"
                  value={form.max_redemptions}
                  onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
                  style={{ fontSize: '16px' }}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Ablaufdatum (optional)</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>
            <Button
              onClick={handleCreate}
              disabled={creating || !form.code || !form.coupon_id}
              className="gap-2 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="create-promo-btn"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              In Stripe erstellen
            </Button>
          </CardContent>
        </Card>

        {/* Aktive Codes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">
              Aktive Codes ({activeCodes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : activeCodes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Noch keine aktiven Promo-Codes</p>
            ) : (
              <div className="space-y-2">
                {activeCodes.map(pc => (
                  <div key={pc.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border bg-background hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={() => copyCode(pc.code)}
                        className="flex items-center gap-2 group"
                        title="Code kopieren"
                      >
                        <code className="text-base font-bold font-mono text-violet-700 dark:text-violet-400">
                          {pc.code}
                        </code>
                        {copied === pc.code
                          ? <Check className="h-3.5 w-3.5 text-green-500" />
                          : <Copy className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        }
                      </button>
                      <div className="text-xs text-muted-foreground hidden sm:block">
                        <span>{pc.discount_label}</span>
                        {pc.times_used > 0 && <span className="ml-2 text-primary">{pc.times_used}× genutzt</span>}
                        {pc.max_uses && <span className="ml-1">/ max. {pc.max_uses}</span>}
                        {pc.expires_at && <span className="ml-2">bis {pc.expires_at}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs text-green-700 border-green-200 hidden sm:flex">
                        Aktiv
                      </Badge>
                      <Button
                        size="icon" variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeactivate(pc)}
                        title="Deaktivieren"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inaktive Codes */}
        {inactiveCodes.length > 0 && (
          <Card className="opacity-60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground">
                Inaktive Codes ({inactiveCodes.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {inactiveCodes.map(pc => (
                  <div key={pc.id} className="flex items-center justify-between p-2 rounded-lg text-sm text-muted-foreground">
                    <code className="font-mono line-through">{pc.code}</code>
                    <span className="text-xs">{pc.discount_label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stripe Coupons (Basis-Rabatte aus Stripe) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Ticket className="h-4 w-4 text-amber-600" />
              Stripe-Coupons ({coupons.length})
              <span className="text-xs text-muted-foreground font-normal">Basis-Rabatte direkt aus Stripe</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : coupons.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3">Keine Coupons in Stripe gefunden</p>
            ) : (
              <div className="space-y-2">
                {coupons.map(c => (
                  <div key={c.id}
                    className={`flex items-start justify-between p-3 rounded-xl border text-sm ${
                      c.valid === false ? 'opacity-50 border-dashed' : 'border-border'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="font-mono font-bold text-amber-700 dark:text-amber-400 text-xs">{c.id}</code>
                        {c.name && c.name !== c.id && <span className="text-xs text-muted-foreground">{c.name}</span>}
                        {c.valid === false && <span className="text-xs bg-gray-100 text-gray-500 rounded px-1.5">Inaktiv</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span className="text-green-700 font-medium">
                          {c.percent_off ? `${c.percent_off}% Rabatt` : c.amount_off ? `${(c.amount_off/100).toFixed(2)} € Rabatt` : ''}
                        </span>
                        <span>·</span>
                        <span>{c.duration === 'repeating' ? `${c.duration_in_months} Monate` : c.duration === 'forever' ? 'Dauerhaft' : 'Einmalig'}</span>
                        {c.times_redeemed > 0 && <><span>·</span><span>{c.times_redeemed}× eingelöst</span></>}
                        {c.max_redemptions && <><span>·</span><span>max. {c.max_redemptions}×</span></>}
                        {c.redeem_by && <><span>·</span><span className="text-orange-600">bis {c.redeem_by}</span></>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
